import { authReturnPath } from "../../../shared/src/site-auth-query";
import { createContext, useEffect, useState } from "react";
import type { SiteShell } from "../server/shell";
import { useRouter } from "@tanstack/react-router";
import { rememberPages } from "./page-memory";

export const PublishedShellContext = createContext<SiteShell | null>(null);

/** Public content paints first; only account/navigation hints wait for the viewer operation. */
export function usePublishedViewer(initial: SiteShell): SiteShell {
  const router = useRouter();
  const [shell, setShell] = useState(initial);
  useEffect(() => {
    setShell(initial);
    if (!initial.publication) return;
    const controller = new AbortController();
    let pending = false;
    type ViewerData = {
      creatorId?: unknown;
      viewer?: { signedIn?: unknown; userId?: unknown; customer?: unknown };
      audience?: unknown;
      planIds?: unknown;
    };
    const apply = (data: ViewerData) => {
      if (
        controller.signal.aborted ||
        data.creatorId !== initial.creator.id ||
        typeof data.viewer?.signedIn !== "boolean" ||
        typeof data.audience !== "string" ||
        !["public", "free", "paid"].includes(data.audience) ||
        !(data.viewer.userId === null || typeof data.viewer.userId === "string")
      )
        return;
      const audience = data.audience as "public" | "free" | "paid";
      const plans = Array.isArray(data.planIds) ? data.planIds : [];
      const signedIn = data.viewer.signedIn;
      const viewer = signedIn ? "signed-in" : "anonymous";
      if (document.documentElement.dataset.viewer !== viewer) {
        document.documentElement.dataset.viewer = viewer;
        rememberPages(router, viewer);
      }
      if (data.viewer.userId) document.documentElement.dataset.playbackUser = data.viewer.userId;
      else delete document.documentElement.dataset.playbackUser;
      setShell({
        ...initial,
        // Items limited to plans stay only for a visitor holding one of them.
        nav: initial.publication!.navigation[audience].filter(
          (item) => !item.planIds || item.planIds.some((id) => plans.includes(id)),
        ),
        viewer: { signedIn, userId: data.viewer.userId },
        header: signedIn
          ? {
              ...initial.header,
              sessionHint: true,
              signUp: false,
              app: "/account",
              appLabel: "Your account",
              homePath: initial.publication!.homePaths[audience],
            }
          : initial.header,
      });
      const home = initial.publication!.homePaths[audience];
      if (signedIn && location.pathname === "/" && home && home !== "/") location.replace(home);
      if (data.viewer.customer && ["/sign-in", "/sign-up"].includes(location.pathname))
        location.replace(authReturnPath(new URLSearchParams(location.search).get("returnTo")));
    };
    const refresh = async () => {
      if (
        pending ||
        document.visibilityState === "hidden" ||
        initial.publication?.enhancedPaths?.includes(location.pathname)
      )
        return;
      pending = true;
      try {
        const response = await fetch(
          `/api/published-site/viewer?creator=${encodeURIComponent(initial.creator.id)}`,
          {
            credentials: "same-origin",
            cache: "no-store",
            signal: controller.signal,
          },
        );
        if (!response.ok) return;
        const data = (await response.json()) as {
          creatorId?: unknown;
          viewer?: { signedIn?: unknown; userId?: unknown };
          audience?: unknown;
        };
        apply(data);
      } catch {
        /* The useful public page remains usable while account hints are unavailable. */
      } finally {
        pending = false;
      }
    };
    const visible = () => {
      void refresh();
    };
    const received = (event: Event) => apply((event as CustomEvent<ViewerData>).detail);
    window.addEventListener("drate:published-viewer", received);
    void refresh();
    const timer = window.setInterval(visible, 30_000);
    window.addEventListener("focus", visible);
    document.addEventListener("visibilitychange", visible);
    let unsubscribe = () => {};
    void import("../../src/lib/published-availability").then(({ watchPublishedCards }) => {
      if (controller.signal.aborted) return;
      const refreshCards = watchPublishedCards(initial.creator.id, controller.signal);
      unsubscribe = router.subscribe("onResolved", () => {
        requestAnimationFrame(refreshCards);
        void refresh();
      });
    });
    return () => {
      controller.abort();
      window.removeEventListener("drate:published-viewer", received);
      window.clearInterval(timer);
      window.removeEventListener("focus", visible);
      document.removeEventListener("visibilitychange", visible);
      unsubscribe();
    };
  }, [initial, router]);
  return shell;
}
